import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import validator from "validator";
import { Box, CircularProgress } from "@mui/material";
import { resetUser } from "../../../../store/features/loginUser";
import { resetAgentUser } from "../../../../store/features/loginUserTenant";
import { clearSessionScopedState } from "../../../../utils/clearSessionScopedState";
import {
  updatePassword,
  resetUpdatePasswordState,
} from "../../../../store/features/updatePassword";
import SettingsContent from "../shared/SettingsContent";
import SettingsPageHeader from "../shared/SettingsPageHeader";
import SettingsPrimaryButton from "../shared/SettingsPrimaryButton";
import SettingsSecondaryButton from "../shared/SettingsSecondaryButton";
import { separatorSx } from "../shared/styles";
import PasswordInput from "./PasswordInput";
import PasswordUpdateSuccessToast from "./PasswordUpdateSuccessToast";
import TwoFactorSection from "./TwoFactorSection";

const REDIRECT_AFTER_MS = 3200;
const TOAST_AUTO_CLOSE_MS = 9000;

const strongPasswordOptions = {
  minLength: 8,
  minLowercase: 1,
  minUppercase: 1,
  minNumbers: 1,
  minSymbols: 1,
};

function AccountSecurityPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const redirectTimerRef = useRef(null);
  const loading = useSelector((state) => state.updatePassword?.loading);

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  useEffect(
    () => () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    },
    [],
  );

  const validate = () => {
    const next = { oldPassword: "", newPassword: "", confirmPassword: "" };
    let valid = true;

    if (!oldPassword.trim()) {
      next.oldPassword = "Current password is required.";
      valid = false;
    }

    if (!newPassword) {
      next.newPassword = "New password is required.";
      valid = false;
    } else if (!validator.isStrongPassword(newPassword, strongPasswordOptions)) {
      next.newPassword =
        "Use at least 8 characters with uppercase, lowercase, number, and symbol.";
      valid = false;
    }

    if (!confirmPassword) {
      next.confirmPassword = "Please confirm your new password.";
      valid = false;
    } else if (newPassword !== confirmPassword) {
      next.confirmPassword = "New password and confirmation do not match.";
      valid = false;
    }

    setErrors(next);
    return valid;
  };

  const isPasswordFormFilled = Boolean(
    oldPassword.trim() && newPassword.trim() && confirmPassword.trim(),
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const result = await dispatch(
        updatePassword({ oldPassword, newPassword, confirmPassword }),
      ).unwrap();

      const successMessage =
        result?.message ||
        "Password updated successfully. All sessions have been cleared; please log in again.";

      toast.success(<PasswordUpdateSuccessToast message={successMessage} />, {
        toastId: "password-update-success",
        className: "toast-password-update-success",
        autoClose: TOAST_AUTO_CLOSE_MS,
        closeOnClick: true,
      });

      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setErrors({ oldPassword: "", newPassword: "", confirmPassword: "" });
      dispatch(resetUpdatePasswordState());

      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
      redirectTimerRef.current = setTimeout(() => {
        redirectTimerRef.current = null;
        toast.dismiss("password-update-success");
        dispatch(resetUser());
        dispatch(resetAgentUser());
        clearSessionScopedState();
        localStorage.removeItem("auth_token");
        localStorage.setItem("logout", Date.now());
        navigate("/login", { replace: true });
      }, REDIRECT_AFTER_MS);
    } catch (err) {
      const message =
        err?.message ||
        (typeof err === "string" ? err : "Could not update password.");
      toast.error(message);
    }
  };

  return (
    <SettingsContent>
      <SettingsPageHeader
        title="Account Security"
        description="Manage your password and two-factor authentication"
      />

      <Box component="form" onSubmit={handleSubmit} sx={{ mt: "32px" }}>
        <PasswordInput
          label="Current Password"
          placeholder="Enter your current password"
          value={oldPassword}
          onChange={(e) => {
            setOldPassword(e.target.value);
            if (errors.oldPassword) {
              setErrors((prev) => ({ ...prev, oldPassword: "" }));
            }
          }}
          error={errors.oldPassword}
        />
        <PasswordInput
          label="New Password"
          placeholder="Enter your new password"
          value={newPassword}
          onChange={(e) => {
            setNewPassword(e.target.value);
            if (errors.newPassword || errors.confirmPassword) {
              setErrors((prev) => ({
                ...prev,
                newPassword: "",
                confirmPassword: "",
              }));
            }
          }}
          error={errors.newPassword}
        />
        <PasswordInput
          label="Confirm New Password"
          placeholder="Enter your confirm new password"
          value={confirmPassword}
          onChange={(e) => {
            setConfirmPassword(e.target.value);
            if (errors.confirmPassword) {
              setErrors((prev) => ({ ...prev, confirmPassword: "" }));
            }
          }}
          error={errors.confirmPassword}
        />
        {isPasswordFormFilled ? (
          <SettingsPrimaryButton
            type="submit"
            disabled={loading}
            sx={{ gap: "8px", width: "168px" }}
          >
            {loading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : null}
            Update Password
          </SettingsPrimaryButton>
        ) : (
          <SettingsSecondaryButton
            type="submit"
            disabled={loading}
            sx={{ gap: "8px", color: "#405261", width: "168px" }}
          >
            {loading ? <CircularProgress size={18} sx={{ color: "#061522" }} /> : null}
            Update Password
          </SettingsSecondaryButton>
        )}
      </Box>

      <Box sx={separatorSx} />

      <TwoFactorSection />
    </SettingsContent>
  );
}

export default AccountSecurityPage;
